import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { classifierApi } from '../api/classifiers';
import type { ClassifierConfig } from '../api/admin';
import { useAuth } from './AuthContext';

/**
 * Runtime classifier registry, shared across the whole app.
 *
 * Why a context (not a per-component fetch):
 *   - MemberDetail (filter dropdowns + card preview) and DocumentView
 *     (chip sections) both read the same registry. A context keeps the
 *     `/api/classifiers/config` round-trip to one per session instead
 *     of one per route.
 *   - The admin Classifications page calls `refresh()` after every
 *     mutation so the rest of the UI picks up the change without a
 *     hard reload.
 *
 * TTL: a 60s in-memory window past the last successful fetch. Past
 * the window, the next read triggers a background refresh; during
 * that window the cached value is returned synchronously.
 *
 * Auth gating: the fetch is deferred until the AuthContext settles
 * with a signed-in user. Login / register / share-accept pages share
 * the App.tsx tree, but the endpoint is authenticated — firing it on
 * those pages would 401-loop via the axios interceptor.
 */

const TTL_MS = 60_000;

interface ClassifiersConfigContextValue {
  /** Active classifiers from the most recent successful fetch.
   *  Empty array until the first fetch lands. Pre-sorted by display_order. */
  classifiers: ClassifierConfig[];
  /** True while the first fetch (or a manual refresh) is in flight. */
  loading: boolean;
  /** Last fetch error message, if any; cleared on the next success. */
  error: string | null;
  /** Force a re-fetch (admin page calls this after every CRUD). */
  refresh: () => Promise<void>;
}

const ClassifiersConfigContext = createContext<ClassifiersConfigContextValue | undefined>(
  undefined
);

const sortByDisplayOrder = (a: ClassifierConfig, b: ClassifierConfig) => {
  if (a.display_order !== b.display_order) return a.display_order - b.display_order;
  return a.label_ro.localeCompare(b.label_ro, 'ro');
};

export const ClassifiersConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [classifiers, setClassifiers] = useState<ClassifierConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastFetchAtRef = useRef<number>(0);
  const inflightRef = useRef<Promise<void> | null>(null);

  const doFetch = useCallback(async () => {
    if (inflightRef.current) return inflightRef.current;
    const promise = (async () => {
      setLoading(true);
      try {
        const list = await classifierApi.config();
        setClassifiers([...list].sort(sortByDisplayOrder));
        lastFetchAtRef.current = Date.now();
        setError(null);
      } catch (err: unknown) {
        const e = err as { response?: { data?: { detail?: string } }; message?: string };
        setError(e.response?.data?.detail || e.message || 'Eroare la încărcarea clasificatorilor');
      } finally {
        setLoading(false);
        inflightRef.current = null;
      }
    })();
    inflightRef.current = promise;
    return promise;
  }, []);

  // Initial fetch + reactive re-fetch on login. Gated on `user` so we
  // never hit /api/classifiers/config from an unauthenticated page.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      // On logout, drop the cached registry so the next login starts clean
      // and the auth-gated dropdowns don't show stale data.
      setClassifiers([]);
      lastFetchAtRef.current = 0;
      return;
    }
    const stale = Date.now() - lastFetchAtRef.current > TTL_MS;
    if (stale) {
      void doFetch();
    }
  }, [user, authLoading, doFetch]);

  const refresh = useCallback(async () => {
    // Manual refresh bypasses the TTL — the admin page calls this right
    // after a mutation, so we want the freshest data immediately.
    lastFetchAtRef.current = 0;
    await doFetch();
  }, [doFetch]);

  return (
    <ClassifiersConfigContext.Provider value={{ classifiers, loading, error, refresh }}>
      {children}
    </ClassifiersConfigContext.Provider>
  );
};

/** Read the runtime classifier registry. Returns an empty list while the
 *  first fetch is in flight; consumers can render skeletons keyed on
 *  `loading` if they care, or just iterate the empty array. */
export const useClassifiersConfig = (): ClassifiersConfigContextValue => {
  const ctx = useContext(ClassifiersConfigContext);
  if (!ctx) {
    throw new Error('useClassifiersConfig must be used within a ClassifiersConfigProvider');
  }
  return ctx;
};
